import is from '@sindresorhus/is';
import { Fragment, type FunctionComponent, type ReactNode, useMemo, useRef } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { AssistantBubble } from '~/ui/domain/chat/AssistantBubble';
import { ThinkingCycle } from '~/ui/domain/chat/ThinkingCycle';
import { UserBubble } from '~/ui/domain/chat/UserBubble';

import type { AgentChatMessage, AgentChatMessagePage, AssistantMessageEntry } from '../../data-contract/chat';
import { useScrollToBottom } from '../hooks/use-scroll-to-bottom';
import { renderInlineNode } from '../structured-renderers';

type ChatMessageListProps = {
    readonly isLoading: boolean;
    readonly pages: readonly AgentChatMessagePage[];
};

const getUserText = (message: AgentChatMessage): string => {
    if (!is.undefined(message.text)) return message.text;
    if (is.undefined(message.entries)) return '';
    return message.entries
        .filter((entry) => entry.type === 'text')
        .map((entry) => entry.text)
        .join('');
};

const renderEntry = (entry: AssistantMessageEntry, index: number, isLastStreaming: boolean): ReactNode => {
    if (entry.type === 'text') {
        if (entry.text.length === 0) return null;
        return (
            <AssistantBubble key={`text-${index}`}>
                <AssistantBubble.Text streaming={isLastStreaming}>{entry.text}</AssistantBubble.Text>
            </AssistantBubble>
        );
    }
    const node = renderInlineNode(entry.envelope, { interactive: true });
    if (is.nullOrUndefined(node)) return null;
    return <Fragment key={entry.envelope.eventId}>{node}</Fragment>;
};

const renderAssistantMessage = (
    message: AgentChatMessage,
    errorMessage: string | null,
    thinkingTexts: readonly [string, ...string[]],
): ReactNode => {
    if (!is.null(errorMessage)) {
        return (
            <AssistantBubble key={message.id}>
                <span className="text-error-700">{errorMessage}</span>
            </AssistantBubble>
        );
    }

    const entries = message.entries ?? [];
    const isStreaming = message.status === 'pending';

    if (entries.length === 0) {
        return isStreaming ? <ThinkingCycle key={message.id} texts={thinkingTexts} /> : null;
    }

    const lastIndex = entries.length - 1;

    return (
        <Fragment key={message.id}>
            {entries.map((entry, index) => renderEntry(entry, index, isStreaming && index === lastIndex))}
        </Fragment>
    );
};

export const ChatMessageList: FunctionComponent<ChatMessageListProps> = ({ isLoading, pages }) => {
    const translate = useTranslate();
    const scrollRef = useRef<HTMLDivElement>(null);

    const messages = useMemo<readonly AgentChatMessage[]>(() => {
        const flattened = pages.flatMap((page) => page.messages);
        return [...flattened].reverse();
    }, [pages]);

    const thinkingTexts = useMemo<readonly [string, ...string[]]>(
        () => [
            translate('chat.thinking-texts.1'),
            translate('chat.thinking-texts.2'),
            translate('chat.thinking-texts.3'),
            translate('chat.thinking-texts.4'),
            translate('chat.thinking-texts.5'),
        ],
        [translate],
    );

    useScrollToBottom(scrollRef, [messages]);

    return (
        <ScrollArea viewportRef={scrollRef} className="min-h-0 w-full flex-1">
            <div className="flex min-h-0 w-full flex-1 flex-col gap-l p-xxl py-l">
                {isLoading && messages.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center">
                        <Spinner className="h-l w-l" />
                    </div>
                ) : null}
                {messages.map((message) =>
                    message.role === 'user' ? (
                        <UserBubble key={message.id}>{getUserText(message)}</UserBubble>
                    ) : (
                        renderAssistantMessage(
                            message,
                            message.status === 'error' ? translate('chat.error-fallback') : null,
                            thinkingTexts,
                        )
                    ),
                )}
            </div>
        </ScrollArea>
    );
};
